import React, { useState, useEffect, useRef } from 'react';
import { Form, ProgressBar, Card, InputGroup, Row, Col } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faExclamationTriangle,
  faRefresh,
  faC
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
// import HVASpinner from 'components/base/HVASpinner';
import {
  fetchCollections,
  uploadDocument,
  fetchDocumentStatus,
  KnowledgeHubCollection
} from '../../services/knowledgehub.service';
import HVAToast from 'components/common/HVAToast';
import docsimage from 'assets/img/spot-illustrations/docs2.png';
import docsimagedark from 'assets/img/spot-illustrations/docs2dark.png';

interface DocumentUploaderProps {
  onUpdate?: () => void;
}

interface UploadingFile {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'success' | 'error';
  error?: string;
  documentId?: string;
}

const DocumentUploader: React.FC<DocumentUploaderProps> = ({ onUpdate }) => {
  const [collections, setCollections] = useState<KnowledgeHubCollection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string>('');
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [uploadingFiles, setUploadingFiles] = useState<
    Map<string, UploadingFile>
  >(new Map());
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('info');
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  // Allowed file types
  const allowedTypes = ['.pdf', '.txt', '.docx', '.html', '.md'];
  const maxFileSize = 50 * 1024 * 1024; // 50MB

  useEffect(() => {
    loadCollections();
  }, []);

  // Helper function to show toast
  const showToastMessage = (
    message: string,
    variant: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const loadCollections = async () => {
    try {
      setLoadingCollections(true);
      const data = await fetchCollections();
      setCollections(data);
      if (data.length > 0 && !selectedCollection) {
        setSelectedCollection(data[0].id);
      }
    } catch (err) {
      showToastMessage('Error loading collections', 'error');
    } finally {
      setLoadingCollections(false);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;

    // Verificar que sean archivos lo que se está arrastrando
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;

    // Solo desactivar cuando realmente salimos del área completa
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Mantener isDragging activo
    if (
      !isDragging &&
      e.dataTransfer.items &&
      e.dataTransfer.items.length > 0
    ) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0; // Reset el contador

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };

  const handleFiles = (files: File[]) => {
    if (!selectedCollection) {
      showToastMessage('Please select a collection first', 'warning');
      return;
    }

    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach(file => {
      // Check file type
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedTypes.includes(ext)) {
        errors.push(
          `${file.name}: Invalid file type. Allowed: ${allowedTypes.join(', ')}`
        );
        return;
      }

      // Check file size
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: File too large (max 50MB)`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      showToastMessage(errors.join(' | '), 'error');
    }

    if (validFiles.length > 0) {
      validFiles.forEach(file => uploadFile(file));
    }
  };

  const uploadFile = async (file: File) => {
    const fileId = `${file.name}-${Date.now()}`;

    // Add to uploading files
    setUploadingFiles(prev =>
      new Map(prev).set(fileId, {
        file,
        progress: 0,
        status: 'uploading'
      })
    );

    try {
      const document = await uploadDocument(
        selectedCollection,
        file,
        progressEvent => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadingFiles(prev => {
            const newMap = new Map(prev);
            const current = newMap.get(fileId);
            if (current) {
              newMap.set(fileId, { ...current, progress });
            }
            return newMap;
          });
        }
      );

      // Update status to processing
      setUploadingFiles(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(fileId);
        if (current) {
          newMap.set(fileId, {
            ...current,
            status: 'processing',
            documentId: document.id,
            progress: 100
          });
        }
        return newMap;
      });

      // Start polling for document status
      pollDocumentStatus(fileId, document.id);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setUploadingFiles(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(fileId);
        if (current) {
          newMap.set(fileId, {
            ...current,
            status: 'error',
            error: err.response?.data?.message || 'Upload failed'
          });
        }
        return newMap;
      });

      // Añadir notificación de error
      showToastMessage(`Failed to upload ${file.name}`, 'error');
    }
  };

  // Añadir un ref para trackear los polling activos
  const activePollingRef = useRef<Set<NodeJS.Timeout>>(new Set());

  // Limpiar todos los polling activos cuando el componente se desmonte
  useEffect(() => {
    return () => {
      // Limpiar todos los timeouts activos
      activePollingRef.current.forEach(timeout => clearTimeout(timeout));
      activePollingRef.current.clear();
    };
  }, []);

  const pollDocumentStatus = async (fileId: string, documentId: string) => {
    const poll = async () => {
      try {
        const status = await fetchDocumentStatus(documentId);

        if (status.status === 'indexed') {
          // Actualizar estado local
          setUploadingFiles(prev => {
            const newMap = new Map(prev);
            const current = newMap.get(fileId);
            if (current) {
              newMap.set(fileId, { ...current, status: 'success' });
            }
            return newMap;
          });

          showToastMessage(
            `Document "${status.name}" processed successfully!`,
            'success'
          );

          // Solo llamar onUpdate cuando TERMINE completamente
          if (onUpdate) onUpdate();
        } else if (status.status === 'error') {
          // Manejar error...
          setUploadingFiles(prev => {
            const newMap = new Map(prev);
            const current = newMap.get(fileId);
            if (current) {
              newMap.set(fileId, {
                ...current,
                status: 'error',
                error: status.processingError || 'Processing failed'
              });
            }
            return newMap;
          });

          showToastMessage(
            `Error processing document: ${
              status.processingError || 'Unknown error'
            }`,
            'error'
          );

          // NO llamar onUpdate en caso de error
        } else {
          // Guardar el timeout para poder limpiarlo después
          const timeoutId = setTimeout(poll, 5000);
          activePollingRef.current.add(timeoutId);
        }
      } catch (err) {
        showToastMessage('Error polling document status.', 'error');
        // Reintentar después de 10 segundos en caso de error
        const timeoutId = setTimeout(poll, 10000);
        activePollingRef.current.add(timeoutId);
      }
    };

    poll();
  };

  const clearCompleted = () => {
    setUploadingFiles(prev => {
      const newMap = new Map(prev);
      Array.from(newMap.entries()).forEach(([key, value]) => {
        if (value.status === 'success' || value.status === 'error') {
          newMap.delete(key);
        }
      });
      return newMap;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
      case 'indexed':
        return (
          <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
        );
      case 'error':
        return <FontAwesomeIcon icon={faTimesCircle} className="text-danger" />;
      case 'processing':
      case 'uploading':
        return <FontAwesomeIcon icon={faClock} className="text-warning" />;
      default:
        return (
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-secondary"
          />
        );
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div>
      {/* Toast Notification */}
      <HVAToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />

      {/* Collection Selector */}
      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Select Collection</Form.Label>
            <InputGroup>
              <Form.Select
                value={selectedCollection}
                onChange={e => setSelectedCollection(e.target.value)}
                disabled={loadingCollections || collections.length === 0}
              >
                {collections.length === 0 ? (
                  <option>No collections available</option>
                ) : (
                  collections.map(col => (
                    <option key={col.id} value={col.id}>
                      {col.name} ({col.documentsCount || 0} docs)
                    </option>
                  ))
                )}
              </Form.Select>
              <Button
                variant="phoenix-primary"
                onClick={loadCollections}
                disabled={loadingCollections}
              >
                <FontAwesomeIcon icon={faRefresh} />
              </Button>
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      {/* Upload Area */}
      <Card
        className={`mb-4 bg-body ${isDragging ? 'border-primary' : ''}`}
        style={{ borderStyle: 'dashed', borderWidth: 2 }}
      >
        <Card.Body
          className="text-center py-5"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <img
            src={docsimage}
            alt={`Supported formats`}
            width="190px"
            className="d-dark-none pe-1"
          />
          <img
            src={docsimagedark}
            alt={`Supported formats`}
            width="190px"
            className="d-light-none pe-1"
          />
          <h6 className="text-body-emphasis">Drag & Drop Files Here</h6>
          <p className="text-body-secondary fs-9">
            or click the button below to select files
          </p>
          <input
            type="file"
            id="file-upload"
            multiple
            accept={allowedTypes.join(',')}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            disabled={!selectedCollection}
          />
          <label htmlFor="file-upload">
            <Button
              as="span"
              variant="primary"
              disabled={!selectedCollection}
              style={{ cursor: selectedCollection ? 'pointer' : 'not-allowed' }}
            >
              <FontAwesomeIcon icon={faFileAlt} className="me-2" />
              Select Files
            </Button>
          </label>
          <p className="text-body-tertiary mt-3 mb-0">
            <small>
              Supported formats: {allowedTypes.join(', ')} | Max size: 10MB
            </small>
          </p>
        </Card.Body>
      </Card>

      {/* Uploading Files */}
      {uploadingFiles.size > 0 && (
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center bg-body pt-1 pb-1">
            <span className="fw-bold">
              Uploading Files ({uploadingFiles.size})
            </span>
            <Button size="sm" variant="link" onClick={clearCompleted}>
              Clear Completed
            </Button>
          </Card.Header>
          <Card.Body className="pb-3">
            {Array.from(uploadingFiles.entries()).map(([id, file]) => (
              <div key={id} className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div className="d-flex align-items-center">
                    {getStatusIcon(file.status)}
                    <span className="ms-2 text-body-emphasis fs-9">
                      {file.file.name}
                    </span>
                    <Badge bg="secondary" className="ms-2" variant="phoenix">
                      {formatFileSize(file.file.size)}
                    </Badge>
                  </div>
                  <span className="text-body-tertiary small">
                    {file.status === 'uploading' && `${file.progress}%`}
                    {file.status === 'processing' && (
                      <>
                        <FontAwesomeIcon icon={faC} className="me-1" spin />
                        Processing...
                      </>
                    )}
                    {file.status === 'success' && 'Complete'}
                    {file.status === 'error' && file.error}
                  </span>
                </div>
                {(file.status === 'uploading' ||
                  file.status === 'processing') && (
                  <ProgressBar
                    animated={file.status === 'processing'}
                    striped={file.status === 'processing'}
                    now={file.progress}
                    variant={
                      file.status === 'processing' ? 'warning' : 'primary'
                    }
                  />
                )}
              </div>
            ))}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default DocumentUploader;
